import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Field, PasswordInput, TextInput } from "@/components/auth/Field";
import { Spinner } from "@/components/common/FullScreenLoader";
import { routeForAuth, useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

export default function SignIn() {
  const navigate = useNavigate();
  const { applyAuth } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      applyAuth(data);
      navigate(routeForAuth(data), { replace: true });
    } catch (e) {
      const err = apiError(e);
      if (err.code === "too_many_attempts" || err.code === "rate_limited") {
        setError(`Too many attempts. Try again in ${Math.ceil((err.retryAfter || 60) / 60)} minute(s).`);
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      eyebrow="Sign in"
      title="Welcome back."
      subtitle="Use your BIZII email and password. Staff accounts continue to an authenticator step."
      footer={
        <p data-testid="signin-footer-note">
          Access is provisioned by BIZII. Need help?{" "}
          <span className="text-slate-300">Contact your network owner.</span>
        </p>
      }
    >
      <form data-testid="signin-form" onSubmit={submit} className="space-y-5" noValidate>
        <Field id="email" label="Email">
          <TextInput
            id="email"
            type="email"
            autoComplete="username"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="signin-email-input"
          />
        </Field>
        <Field
          id="password"
          label="Password"
          hint={
            <Link to="/forgot-password" data-testid="signin-forgot-link" className="text-xs text-slate-400 transition-colors hover:text-electric">
              Forgot password?
            </Link>
          }
        >
          <PasswordInput
            id="password"
            autoComplete="current-password"
            placeholder="Your passphrase"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            testId="signin-password-input"
          />
        </Field>
        {error ? (
          <div role="alert" data-testid="signin-error" className="bz-alert-error">
            {error}
          </div>
        ) : null}
        <button type="submit" disabled={busy || !email || !password} data-testid="signin-submit-button" className="bz-btn-primary w-full">
          {busy ? <Spinner /> : null}
          {busy ? "Signing in" : "Sign in"}
          {!busy ? <ArrowRight className="h-4 w-4" strokeWidth={2} /> : null}
        </button>
      </form>
    </AuthLayout>
  );
}
